import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';

import { v4 as uuidv4 } from "uuid";

import { 
  Feature,
  FeatureBase,
} from "@models";
import { 
  FeatureBaseListEnhancement,
  TransformUpgradeFeature,
} from "@dndModels";
import { CREATURE_FEATURE_TYPES } from "@dndModels/Constants";
import { CreatureFBType } from '@dndModels/Types';

import CommonUtilities from "@utilities/common_utilities";

import EnhancementModeBox from './EnhancementModeBox';
import FeatureBaseInput from './FeatureBaseInput';
import FeatureBasesInput from './FeatureBasesInput';

const common_util = CommonUtilities.getInstance();

interface Props {
  feature_source: TransformUpgradeFeature;
  creature_fb_type: CreatureFBType;
  value: FeatureBaseListEnhancement;
  disabled: boolean;
  onChange: () => void;
}

// Adapted from TransformOptionInput's renderFBs, operating on a FeatureBaseListEnhancement.
//
// NOTE: assumes a "transform_upgrade" value exists for feature_source_type, alongside
// "summon" - confirm that against however feature_source_type is actually defined
// before relying on this.
export default function FeatureBaseListEnhancementInput({
  feature_source,
  creature_fb_type,
  value,
  disabled,
  onChange
}: Props) {
  const [expanded_feature_base, setExpandedFeatureBase] = useState<FeatureBase | null>(null);
  const value_disabled = disabled || value.mode === "Leave";

  const filtered = value.value.filter(o => o.creature_fb_type === creature_fb_type);

  if (expanded_feature_base) {
    const feature = expanded_feature_base.features[0];
    if (!feature) {
      return <>Error</>;
    }
    return (
      <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
        <FeatureBaseInput
          parent_name={feature_source.name}
          feature_base={expanded_feature_base}
          feature_bases={filtered}
          feature_source={feature_source}
          disabled={value_disabled}
          simple
          allowed_feature_types={[...CREATURE_FEATURE_TYPES,"Minion Ability"]}
          onChange={(changed: FeatureBase) => {
            changed.creature_fb_type = creature_fb_type;
            let objFinder = value.value.filter(o => o.id === changed.id);
            if (objFinder.length === 1) {
              objFinder[0].copy(changed);
            } else {
              if (common_util.unique_array(objFinder.map(o => 
                o.features[0].floating_feature_id)).length === 1) {
                // Something went wrong, causing it to duplicate one of them.
                // Delete them and then add changed.
                value.value = value.value.filter(o => o.id !== changed.id);
                value.value.push(changed);
              } else {
                // Give one of them a new ID. They probably have different levels, so use that.
                objFinder = objFinder.filter(o => o.level === changed.level);
                if (objFinder.length === 1) {
                  objFinder[0].copy(changed);
                  const others = value.value.filter(o => o.id === changed.id && o.level !== changed.level);
                  for (const other of others) {
                    other.true_id = uuidv4().toString();
                  }
                } else {
                  // Just pick one.
                  objFinder[0].copy(changed);
                  for (let i = 1; i < objFinder.length; ++i) {
                    const other = objFinder[i];
                    if (other) {
                      other.true_id = uuidv4().toString();
                    }
                  }
                }
              }
            }
            onChange();
          }}
          onDelete={() => {
            const id = expanded_feature_base.id;
            value.value = value.value.filter(o => o.id !== id);
            setExpandedFeatureBase(null);
            onChange();
          }}
          onDone={() => {
            setExpandedFeatureBase(null);
          }}
        />
      </div>
    );
  }

  const height = window.innerHeight - 64;
  return (
    <Grid container spacing={0} direction="row">
      <Grid size={12}>
        <EnhancementModeBox
          name="Mode"
          modes={["Leave","Replace","Add"]}
          value={value.mode}
          disabled={disabled}
          onChange={(changed) => {
            value.mode = changed as FeatureBaseListEnhancement["mode"];
            onChange();
          }}
        />
      </Grid>
      <Grid size={12}>
        <FeatureBasesInput 
          feature_bases={filtered} 
          feature_source={feature_source}
          disabled={value_disabled}
          onExpand={setExpandedFeatureBase}
          height={height}
          onAdd={() => {
            const new_fb = new FeatureBase();
            new_fb.creature_fb_type = creature_fb_type;
            new_fb.feature_source_id = feature_source._id;
            new_fb.feature_source_type = "transform_upgrade";
            const new_f = new Feature();
            new_f.parent_type = new_fb.feature_source_type;
            new_f.parent_id = new_fb.feature_source_id;
            new_f.base_id = new_fb.id;
            new_fb.features.push(new_f);
            value.value.push(new_fb);
            onChange();
            setExpandedFeatureBase(new_fb);
          }}
          onReorder={(updated) => {
            for (const u of updated) {
              const fb = value.value.find(o => o.true_id === u.true_id);
              if (fb) {
                fb.copy(u);
              }
            }
            onChange();
          }}
        />
      </Grid>
    </Grid>
  );
}
